import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import {
  LayoutDashboard, Network, Workflow, Boxes, Package, ListChecks, Gauge,
  Play, Pause, SkipForward, SkipBack, Square, X, Plus, Search, Wand2,
  ChevronRight, ChevronDown, ChevronLeft, Check, Circle, GitBranch, Server, Trash2,
  CheckCircle2, CircleDot, Download, Layers, FileDown, FileCode, FileText, RotateCcw,
  GripHorizontal, ExternalLink, Maximize2, Minimize2,
} from "lucide-react";
import { exportScenario } from "./makeHtml.js";

import {
  T, FONT_DISPLAY, FONT_BODY, FONT_MONO, LANES, LANE_COLOR,
  LANE_Y, LANE_H, GUT, CANVAS_W, CANVAS_H, NODE_W, NODE_H, laneMid,
} from "./clmConfig.jsx";
import { SEED_SYSTEMS, ROLES, CHANNELS, VENDORS } from "./clmSystems.jsx";
import { BUSINESS_OBJECTS, BO_COLOR } from "./clmBusinessObjects.jsx";
import { STORIES, FAM_META, STORY_COUNT } from "./clmStories.jsx";
import { PHASES, MATURITY, ARTIFACT_TOTAL } from "./clmImplementation.jsx";
import { INTERFACES, IFACE_BY_ID, STORY_INTERFACES, ifacesForStory } from "./clmInterfaces.jsx";

/* ============================================================
   GEOMETRY HELPERS  (layout = optional {id:{x,y}} drag overrides)
   ============================================================ */
function sysCenter(sys, layout) {
  const o = layout && layout[sys.id];
  return o ? { x: o.x, y: o.y } : { x: sys.x, y: laneMid(sys.level) };
}
function edgePath(a, b, layout) {
  const A = sysCenter(a, layout), B = sysCenter(b, layout);
  const ah = NODE_H[a.role] || 60, bh = NODE_H[b.role] || 60;
  if (a.level === b.level) {
    const sy = A.y - ah / 2, ey = B.y - bh / 2, dx = B.x - A.x, lift = 24 + Math.abs(dx) * 0.08;
    return `M ${A.x} ${sy} C ${A.x + dx * 0.25} ${sy - lift}, ${B.x - dx * 0.25} ${ey - lift}, ${B.x} ${ey}`;
  }
  const down = B.y > A.y;
  const sy = A.y + (down ? ah / 2 : -ah / 2), ey = B.y + (down ? -bh / 2 : bh / 2), dy = ey - sy;
  return `M ${A.x} ${sy} C ${A.x} ${sy + dy * 0.45}, ${B.x} ${ey - dy * 0.45}, ${B.x} ${ey}`;
}
/* resolve a story's steps for "without CPI" (PLMSI direct to SAP) */
function resolveSteps(story, cpiMode) {
  if (cpiMode) return story.steps;
  const out = [];
  for (const st of story.steps) {
    let f = st.f, t = st.t, note = st.note;
    if (t === "CPI") { t = "ERP"; if (!/SAP/.test(note)) note += " (PLMSI direct to SAP — no CPI.)"; }
    else if (f === "CPI") { f = "ERP"; note = note.replace(/CPI/g, "SAP"); }
    if (f === t) continue;
    out.push({ f, t, bo: st.bo, note });
  }
  const dd = [];
  for (const st of out) { const p = dd[dd.length - 1]; if (p && p.f === st.f && p.t === st.t && p.bo === st.bo) continue; dd.push(st); }
  return dd;
}

/* ============================================================
   SHARED UI PRIMITIVES
   ============================================================ */
const Panel = ({ children, style }) => (
  <div style={{ background: T.panel, border: `1px solid ${T.line}`, borderRadius: 14, ...style }}>{children}</div>
);
const Pill = ({ children, color, bg, style, onClick, active }) => (
  <span onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: 6, fontFamily: FONT_MONO, fontSize: 11, fontWeight: 600,
    padding: "4px 10px", borderRadius: 20, color: color || T.ink2,
    background: bg || (active ? color + "22" : "transparent"),
    border: `1px solid ${active ? color : T.line}`, cursor: onClick ? "pointer" : "default", ...style,
  }}>{children}</span>
);
const Btn = ({ children, onClick, active, color = T.plmsi, style }) => (
  <button onClick={onClick} style={{
    display: "inline-flex", alignItems: "center", gap: 7, fontFamily: FONT_BODY, fontSize: 12.5, fontWeight: 600,
    padding: "8px 13px", borderRadius: 9, cursor: "pointer", transition: "all .15s",
    color: active ? "#06101d" : T.ink, background: active ? color : T.panel,
    border: `1px solid ${active ? color : T.line}`, ...style,
  }}>{children}</button>
);
const SectionTitle = ({ icon, title, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
      {icon}<h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 20, color: T.ink, margin: 0 }}>{title}</h2>
    </div>
    {sub && <p style={{ color: T.ink2, fontSize: 13, marginTop: 6, maxWidth: 920, lineHeight: 1.5 }}>{sub}</p>}
  </div>
);

/* ============================================================
   SWIM-LANE SVG  (shared: Architecture · Flow Studio · Setup preview)
   ============================================================ */
function SwimLaneDiagram({
  systems, overlays, activeSteps, seqOn, tokenStep, dimSet, onHoverNode,
  selectable, selectedSet, onToggleNode, layout, onDragNode, accent,
}) {
  const sysById = useMemo(() => Object.fromEntries(systems.map(s => [s.id, s])), [systems]);
  const svgRef = useRef(null);
  const dragRef = useRef(null);
  const bx = useMemo(() => {
    const cn4s = sysById.CN4S, cpi = sysById.CPI;
    const cx = id => sysCenter(sysById[id], layout).x;
    return (cn4s && cpi) ? (cx("CN4S") + cx("CPI")) / 2 + 8 : 800;
  }, [sysById, layout]);

  function toSvg(evt) {
    const svg = svgRef.current; if (!svg || !svg.createSVGPoint) return { x: evt.clientX, y: evt.clientY };
    const p = svg.createSVGPoint(); p.x = evt.clientX; p.y = evt.clientY;
    const m = svg.getScreenCTM(); return m ? p.matrixTransform(m.inverse()) : { x: evt.clientX, y: evt.clientY };
  }
  function onPointerDown(e, id) {
    if (!onDragNode) { if (selectable && onToggleNode) onToggleNode(id); return; }
    dragRef.current = { id, moved: false };
    try { e.target.setPointerCapture && e.target.setPointerCapture(e.pointerId); } catch (x) {}
  }
  function onPointerMove(e) {
    if (!dragRef.current || !onDragNode) return;
    dragRef.current.moved = true; const p = toSvg(e); onDragNode(dragRef.current.id, p.x, p.y);
  }
  function onPointerUp(e) {
    if (!dragRef.current) return;
    const { id, moved } = dragRef.current; dragRef.current = null;
    if (!moved && selectable && onToggleNode) onToggleNode(id);
  }

  const baseFlows = useMemo(() => {
    const F = []; const have = id => sysById[id];
    const add = (f, t, layer, bo) => { if (have(f) && have(t)) F.push({ f, t, layer, bo }); };
    add("TC", "CN4T", "plmsi"); add("CN4T", "MES", "plmsi", "BOP");
    add("TC", "AIG", "plmsi"); add("AIG", "CPI", "boundary");
    add("MES", "CN4S", "plmsi"); add("CN4S", "CPI", "boundary");
    add("CPI", "ERP", "cpi", "Production Order"); add("CPI", "SUP", "cpi");
    add("MES", "APS", "bo"); add("APS", "CN4S", "bo", "Optimized Plan"); add("HUB", "MES", "bo");
    add("MES", "OEE", "bo", "OEE Data"); add("OEE", "OINT", "bo", "OEE Data");
    add("MES", "OINT", "bo", "Report"); add("QM", "OINT", "bo", "Inspection Result");
    add("MES", "EDGE", "bo"); add("MES", "QM", "bo"); add("MES", "SFC", "bo", "Work Instructions");
    add("EDGE", "CNC", "bo"); add("EDGE", "PLC", "bo"); add("EDGE", "IIOT", "bo");
    add("PLC", "CONV", "bo"); add("CNC", "PROC", "bo"); add("ROB", "PROC", "bo"); add("IIOT", "PROC", "bo");
    return F;
  }, [sysById]);

  const COL = { plmsi: T.plmsi, cpi: T.cpi, boundary: T.boundary, bo: "#5b7693" };
  const story = activeSteps && activeSteps.length;

  return (
    <svg ref={svgRef} viewBox={`0 0 ${CANVAS_W} ${CANVAS_H}`} preserveAspectRatio="xMidYMid meet"
      onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerLeave={onPointerUp}
      style={{ width: "100%", height: "100%", display: "block" }}>
      <defs>
        {LANES.map(L => (
          <linearGradient key={L.id} id={`g-${L.id}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor={LANE_COLOR[L.id]} stopOpacity="0.10" />
            <stop offset="1" stopColor={LANE_COLOR[L.id]} stopOpacity="0.02" />
          </linearGradient>
        ))}
        <filter id="glow" x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation="3" result="b" /><feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {LANES.map(L => (
        <g key={L.id}>
          <rect x={GUT} y={LANE_Y[L.id]} width={CANVAS_W - GUT - 16} height={LANE_H[L.id]} fill={`url(#g-${L.id})`} stroke={LANE_COLOR[L.id] + "30"} rx={10} />
          <rect x={GUT} y={LANE_Y[L.id]} width={4} height={LANE_H[L.id]} fill={LANE_COLOR[L.id]} opacity={0.55} />
          <text x={22} y={laneMid(L.id) - 6} fill={LANE_COLOR[L.id]} fontFamily={FONT_DISPLAY} fontWeight="800" fontSize="26">{L.id}</text>
          <text x={22} y={laneMid(L.id) + 13} fill="#cfe2f5" fontFamily={FONT_MONO} fontSize="10.5" fontWeight="600">{L.label}</text>
          <text x={22} y={laneMid(L.id) + 28} fill="#5b7693" fontFamily={FONT_BODY} fontSize="9.5">{L.sub}</text>
        </g>
      ))}

      {overlays.maturity && (
        <g>
          {[[70, 350, "#ffcc4d", "Strategic CLM (L3)"], [440, 140, "#ff8a3d", "Event-driven (L4)"], [610, 270, "#22d3c5", "Federated (L4+)"]].map(([y, h, c, d], i) => (
            <g key={i}>
              <rect x={GUT} y={y} width={CANVAS_W - GUT - 16} height={h} fill={c} opacity={0.05} />
              <text x={CANVAS_W - 26} y={y + 16} fill={c} textAnchor="end" fontFamily={FONT_MONO} fontSize="10.5" fontWeight="600">{d}</text>
            </g>
          ))}
        </g>
      )}

      {!story && baseFlows.map((fl, i) => {
        const a = sysById[fl.f], b = sysById[fl.t]; if (!a || !b) return null;
        const lyr = fl.layer === "boundary" ? "cpi" : fl.layer;
        if (lyr !== "bo" && overlays && !overlays[lyr]) return null;
        return <path key={i} d={edgePath(a, b, layout)} fill="none" stroke={COL[fl.layer] || "#5b7693"}
          strokeWidth={fl.layer === "bo" ? 1.5 : 2.4} strokeOpacity={fl.layer === "bo" ? 0.42 : 0.8}
          strokeLinecap="round" strokeDasharray={fl.layer === "boundary" ? "7 6" : "none"} />;
      })}

      {story && activeSteps.map((st, i) => {
        const a = sysById[st.f], b = sysById[st.t]; if (!a || !b) return null;
        const c = BO_COLOR[st.bo] || accent || "#5be584";
        return <path key={"seg" + i} d={edgePath(a, b, layout)} fill="none" stroke={c} strokeWidth={3} strokeOpacity={0.5} strokeLinecap="round" />;
      })}
      {story && seqOn && activeSteps.map((st, i) => {
        const a = sysById[st.f], b = sysById[st.t]; if (!a || !b) return null;
        const A = sysCenter(a, layout), B = sysCenter(b, layout); const mx = (A.x + B.x) / 2, my = (A.y + B.y) / 2;
        const c = BO_COLOR[st.bo] || accent || "#5be584";
        return <g key={"badge" + i} transform={`translate(${mx},${my})`}>
          <circle r={11} fill="#06101d" stroke={c} strokeWidth={2} />
          <text x={0} y={4} textAnchor="middle" fill={c} fontFamily={FONT_DISPLAY} fontWeight="800" fontSize="12">{i + 1}</text>
        </g>;
      })}

      {overlays && overlays.boundary && sysById.CN4S && sysById.CPI && (
        <g>
          <line x1={bx} y1={54} x2={bx} y2={420} stroke={T.boundary} strokeWidth={2.4} strokeDasharray="7 6" />
          <text x={bx - 10} y={48} fill={T.plmsi} textAnchor="end" fontFamily={FONT_MONO} fontSize="11" fontWeight="600">◄ SIEMENS / PLMSI</text>
          <text x={bx + 10} y={48} fill={T.cpi} fontFamily={FONT_MONO} fontSize="11" fontWeight="600">SAP / CPI ►</text>
          <rect x={bx - 74} y={392} width={148} height={26} rx={13} fill="#1a0a10" stroke={T.boundary} />
          <text x={bx} y={409} fill="#ff6b86" textAnchor="middle" fontFamily={FONT_MONO} fontSize="10" fontWeight="600">◆ DEMARCATION</text>
        </g>
      )}

      {systems.map(s => {
        const c = s.color; const w = NODE_W[s.role] || 120, h = NODE_H[s.role] || 60;
        const cen = sysCenter(s, layout);
        const offByDim = dimSet && !dimSet.has(s.id);
        const offBySel = selectable && selectedSet && !selectedSet.has(s.id);
        const dim = offByDim || offBySel;
        const sel = selectable && selectedSet && selectedSet.has(s.id);
        const shape =
          s.role === "bus" ? <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={h / 2} fill="#1a0e08" stroke={c} strokeWidth={sel ? 3.2 : 2.2} filter="url(#glow)" />
          : s.role === "gateway" ? <polygon points={`${-w / 2 + 14},${-h / 2} ${w / 2 - 14},${-h / 2} ${w / 2},0 ${w / 2 - 14},${h / 2} ${-w / 2 + 14},${h / 2} ${-w / 2},0`} fill="#0c1a2b" stroke={c} strokeWidth={sel ? 3 : 1.8} />
          : s.role === "device" ? <polygon points={`${-w / 2 + 12},${-h / 2} ${w / 2 - 12},${-h / 2} ${w / 2},${-h / 2 + 12} ${w / 2},${h / 2 - 12} ${w / 2 - 12},${h / 2} ${-w / 2 + 12},${h / 2} ${-w / 2},${h / 2 - 12} ${-w / 2},${-h / 2 + 12}`} fill="#0c1a2b" stroke={c} strokeWidth={sel ? 3 : 1.7} />
          : s.role === "physical" ? <polygon points={`${-w / 2 + 12},${-h / 2} ${w / 2 - 12},${-h / 2} ${w / 2},${h / 2} ${-w / 2},${h / 2}`} fill="#0c1a2b" stroke={c} strokeWidth={sel ? 3 : 1.7} />
          : <rect x={-w / 2} y={-h / 2} width={w} height={h} rx={s.role === "hub" ? 12 : 8} fill={s.vendor === "SAP" ? "#1a140a" : "#0c1a2b"} stroke={c} strokeWidth={sel ? 3.2 : (s.role === "hub" ? 2.2 : 1.7)} strokeDasharray={s.role === "external" ? "6 3" : "none"} />;
        return (
          <g key={s.id} transform={`translate(${cen.x},${cen.y})`} opacity={dim ? 0.16 : 1}
            style={{ cursor: onDragNode ? "grab" : "pointer", transition: dragRef.current ? "none" : "opacity .25s" }}
            onMouseEnter={(e) => onHoverNode && onHoverNode(s, e)} onMouseLeave={() => onHoverNode && onHoverNode(null)}
            onPointerDown={(e) => onPointerDown(e, s.id)}>
            {shape}
            <text x={0} y={s.sub ? -2 : 5} textAnchor="middle" fill="#eaf4ff" fontFamily={FONT_DISPLAY} fontWeight="700" fontSize={s.role === "hub" ? 13.5 : 11.5}>{s.name}</text>
            {s.sub && <text x={0} y={13} textAnchor="middle" fill="#7fa0bd" fontFamily={FONT_BODY} fontSize="9">{s.sub}</text>}
            {s.custom && <circle cx={w / 2 - 8} cy={-h / 2 + 8} r={4} fill={T.b2mml} />}
            {selectable && sel && <g transform={`translate(${w / 2 - 10},${-h / 2 + 10})`}><circle r={8} fill={T.b2mml} /><path d="M -3 0 L -1 2 L 3 -3" stroke="#06101d" strokeWidth={2} fill="none" /></g>}
          </g>
        );
      })}

      {tokenStep && (
        <g transform={`translate(${tokenStep.x},${tokenStep.y})`} style={{ transition: "transform 1.2s cubic-bezier(.45,.05,.3,1)" }}>
          <rect x={-(tokenStep.label.length * 6.6 + 18) / 2} y={-12} width={tokenStep.label.length * 6.6 + 18} height={24} rx={12} fill={tokenStep.color} filter="url(#glow)" />
          <text x={0} y={5} textAnchor="middle" fill="#06101d" fontFamily={FONT_MONO} fontWeight="600" fontSize="11.5">{tokenStep.label}</text>
        </g>
      )}
    </svg>
  );
}

/* ============================================================
   FLOW STUDIO  — all 98 BPH stories, CPI toggle, BO filter, auto mode
   `scope` (optional): {systems:Set, stories:Set} from the Setup configurator
   ============================================================ */
function FlowStudio({ systems, scope, flow, setFlow, layout, setLayout, nav }) {
  const sysById = useMemo(() => Object.fromEntries(systems.map(s => [s.id, s])), [systems]);
  const cpiMode = flow.cpiMode, overlays = flow.overlays, seqOn = flow.seqOn, fam = flow.fam, boFilter = flow.boFilter;
  const setCpiMode = (v) => setFlow(f => ({ ...f, cpiMode: typeof v === "function" ? v(f.cpiMode) : v }));
  const setOverlays = (v) => setFlow(f => ({ ...f, overlays: typeof v === "function" ? v(f.overlays) : v }));
  const setSeqOn = (v) => setFlow(f => ({ ...f, seqOn: typeof v === "function" ? v(f.seqOn) : v }));
  const setFam = (v) => setFlow(f => ({ ...f, fam: v }));
  const setBoFilter = (v) => setFlow(f => ({ ...f, boFilter: typeof v === "function" ? v(f.boFilter) : v }));
  const [search, setSearch] = useState("");
  const [scopeOnly, setScopeOnly] = useState(!!(scope && scope.stories && scope.stories.size));
  const activeId = flow.activeId || null;
  const setActiveId = (v) => setFlow(f => ({ ...f, activeId: typeof v === "function" ? v(f.activeId) : v }));
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [auto, setAuto] = useState(false);
  const [hover, setHover] = useState(null);
  const [token, setToken] = useState(null);
  const timerRef = useRef(null);
  const autoQ = useRef([]); const autoI = useRef(0);
  // ── panel / chrome controls (collapsible side panels, dockable player, BPH visibility) ──
  const [sideOpen, setSideOpen] = useState(true);
  const [legendOpen, setLegendOpen] = useState(true);
  const showBph = flow.showBph !== false;
  const setShowBph = (v) => setFlow(f => ({ ...f, showBph: typeof v === "function" ? v(f.showBph !== false) : v }));
  const [dock, setDock] = useState("bottom");           // "bottom" | "float"
  const [fullScreen, setFullScreen] = useState(false);   // maximize central canvas
  const [ifHover, setIfHover] = useState(null);          // {iface, x, y} → attribute tooltip
  const [playerPos, setPlayerPos] = useState({ x: 24, y: 90 });
  const playerDrag = useRef(null);
  function onPlayerHandleDown(e) { playerDrag.current = { sx: e.clientX, sy: e.clientY, ox: playerPos.x, oy: playerPos.y }; window.addEventListener("pointermove", onPlayerMove); window.addEventListener("pointerup", onPlayerUp); }
  function onPlayerMove(e) { const d = playerDrag.current; if (!d) return; setDock("float"); setPlayerPos({ x: Math.max(6, d.ox + (e.clientX - d.sx)), y: Math.max(6, d.oy + (e.clientY - d.sy)) }); }
  function onPlayerUp() { playerDrag.current = null; window.removeEventListener("pointermove", onPlayerMove); window.removeEventListener("pointerup", onPlayerUp); }

  const activeStory = activeId ? STORIES.find(s => s.id === activeId) : null;
  const activeIface = flow.activeIface ? IFACE_BY_ID[flow.activeIface] : null;
  const setActiveIface = (v) => setFlow(f => ({ ...f, activeIface: typeof v === "function" ? v(f.activeIface) : v }));
  // the animated path comes from the interface (if one is selected) else the story
  const focusObj = activeIface || activeStory;
  const steps = activeIface ? activeIface.steps : (activeStory ? resolveSteps(activeStory, cpiMode) : []);

  const filtered = useMemo(() => STORIES.filter(s => {
    if (scopeOnly && scope && scope.stories && scope.stories.size && !scope.stories.has(s.id)) return false;
    if (fam !== "ALL" && s.fam !== fam) return false;
    if (boFilter && !s.steps.some(st => st.bo === boFilter)) return false;
    if (search) { const q = search.toLowerCase(); if (!(String(s.sn).includes(q) || s.id.toLowerCase().includes(q) || s.title.toLowerCase().includes(q))) return false; }
    return true;
  }), [fam, boFilter, search, scopeOnly, scope]);

  const dimSet = useMemo(() => {
    if (!focusObj) return null;
    const set = new Set(); steps.forEach(st => { set.add(st.f); set.add(st.t); }); return set;
  }, [focusObj, steps]);

  const runToken = useCallback((idx) => {
    if (!focusObj) return;
    const st = steps[idx]; if (!st) return;
    const a = sysById[st.f], b = sysById[st.t]; if (!a || !b) return;
    const c = BO_COLOR[st.bo] || (activeIface ? activeIface.color : activeStory.sc);
    const A = sysCenter(a, layout), B = sysCenter(b, layout);
    setToken({ x: A.x, y: A.y, color: c, label: st.bo });
    requestAnimationFrame(() => requestAnimationFrame(() => setToken({ x: B.x, y: B.y, color: c, label: st.bo })));
  }, [focusObj, steps, sysById, layout]);

  function focusStory(id, isAuto = false) {
    clearTimeout(timerRef.current);
    setActiveIface(null); setActiveId(id); setStep(0); setPlaying(true);
    if (!isAuto) setAuto(false);
  }
  function focusIface(ifId, storyId) {
    clearTimeout(timerRef.current);
    setAuto(false);
    if (storyId) setActiveId(storyId);     // keep the story context highlighted
    setActiveIface(ifId); setStep(0); setPlaying(true);
  }
  function clearFocus() {
    clearTimeout(timerRef.current);
    setActiveId(null); setActiveIface(null); setPlaying(false); setAuto(false); setToken(null); setStep(0);
  }
  function advanceAuto() {
    autoI.current += 1;
    if (autoI.current >= autoQ.current.length) { clearFocus(); return; }
    focusStory(autoQ.current[autoI.current].id, true);
  }
  function startAuto() {
    autoQ.current = filtered; autoI.current = 0;
    if (!filtered.length) return;
    setAuto(true); focusStory(filtered[0].id, true);
  }

  useEffect(() => {
    if (!playing || !focusObj) return;
    runToken(step);
    timerRef.current = setTimeout(() => {
      const last = step >= steps.length - 1;
      if (last) { if (auto) advanceAuto(); else setStep(0); }
      else setStep(s => s + 1);
    }, 1700);
    return () => clearTimeout(timerRef.current);
    // eslint-disable-next-line
  }, [playing, step, focusObj, cpiMode]);

  const curStep = steps[step];
  const stepColor = curStep ? (BO_COLOR[curStep.bo] || "#5be584") : "#5be584";
  const toggles = [
    ["cpiMode", cpiMode ? "CPI middleware" : "No CPI (PLMSI direct)", T.cpi, () => { setCpiMode(m => !m); setStep(0); }, cpiMode],
    ["plmsi", "PLMSI", T.plmsi, () => setOverlays(o => ({ ...o, plmsi: !o.plmsi })), overlays.plmsi],
    ["cpi", "CPI", T.cpi, () => setOverlays(o => ({ ...o, cpi: !o.cpi })), overlays.cpi],
    ["boundary", "Demarcation", T.boundary, () => setOverlays(o => ({ ...o, boundary: !o.boundary })), overlays.boundary],
    ["maturity", "Maturity", T.gold, () => setOverlays(o => ({ ...o, maturity: !o.maturity })), overlays.maturity],
    ["seq", "Sequence #", T.b2mml, () => setSeqOn(v => !v), seqOn],
    ["bph", "BPH #", T.tc, () => setShowBph(v => !v), showBph],
  ];
  const hasScope = scope && scope.stories && scope.stories.size > 0;

  return (
    <div style={{ display: "flex", height: "100%", minHeight: 0 }}>
      {!sideOpen && (
        <div onClick={() => setSideOpen(true)} title="Show user stories"
          style={{ width: 30, flex: "none", borderRight: `1px solid ${T.line}`, background: T.bg2, display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 12, gap: 10, cursor: "pointer" }}>
          <ChevronRight size={16} color={T.plmsi} />
          <div style={{ writingMode: "vertical-rl", fontFamily: FONT_MONO, fontSize: 10, letterSpacing: ".18em", color: T.ink2, textTransform: "uppercase" }}>User Stories · BPH ({filtered.length})</div>
        </div>
      )}
      {sideOpen && (
      <aside style={{ width: 300, flex: "none", borderRight: `1px solid ${T.line}`, display: "flex", flexDirection: "column", minHeight: 0, background: T.bg2 }}>
        <div style={{ padding: "14px 14px 8px" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: ".15em", color: T.ink2, textTransform: "uppercase" }}>
              User Stories · BPH <span style={{ color: T.plmsi }}>({filtered.length}/{STORY_COUNT})</span>
            </div>
            <button onClick={() => setSideOpen(false)} title="Collapse panel" style={{ background: "transparent", border: "none", color: T.ink3, cursor: "pointer", padding: 2 }}><ChevronLeft size={16} /></button>
          </div>
          {boFilter && <div style={{ marginTop: 6, fontSize: 10.5, color: T.ink3 }}>filtered by <b style={{ color: BO_COLOR[boFilter] }}>{boFilter}</b> <span onClick={() => setBoFilter(null)} style={{ cursor: "pointer", color: T.cpi }}>✕</span></div>}
          <div style={{ position: "relative", marginTop: 9 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: 9, color: T.ink3 }} />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search serial #, BPH or title…"
              style={{ width: "100%", background: T.panel, border: `1px solid ${T.line}`, borderRadius: 8, color: T.ink, padding: "7px 10px 7px 30px", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }} />
          </div>
          <div style={{ display: "flex", gap: 5, marginTop: 8 }}>
            {["ALL", "T", "O", "K"].map(f => (
              <button key={f} onClick={() => setFam(f)} style={{
                flex: 1, padding: "5px 4px", borderRadius: 7, fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 600, cursor: "pointer",
                color: fam === f ? "#06101d" : T.ink2, background: fam === f ? (f === "ALL" ? "#9fb6cd" : FAM_META[f].color) : T.panel,
                border: `1px solid ${fam === f ? "transparent" : T.line}`,
              }}>{f === "ALL" ? "All" : FAM_META[f].code.replace("BPH.", "")}</button>
            ))}
          </div>
          {hasScope && (
            <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 9, fontSize: 11, color: T.ink2, cursor: "pointer" }}>
              <input type="checkbox" checked={scopeOnly} onChange={e => setScopeOnly(e.target.checked)} style={{ accentColor: T.b2mml }} />
              Show configured scope only ({scope.stories.size})
            </label>
          )}
        </div>
        <div style={{ flex: 1, overflowY: "auto", padding: "0 10px 14px" }}>
          {["T", "O", "K"].map(f => {
            const items = filtered.filter(s => s.fam === f); if (!items.length) return null;
            return (
              <div key={f}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: ".12em", color: T.ink3, textTransform: "uppercase", padding: "10px 4px 5px" }}>{FAM_META[f].name} · {items.length}</div>
                {items.map(s => {
                  const ifaces = ifacesForStory(s.id);
                  return (
                  <div key={s.id} style={{
                    background: T.panel, border: `1px solid ${activeId === s.id ? s.sc : T.line}`, borderLeft: `3px solid ${s.sc}`,
                    borderRadius: 9, padding: "9px 11px", marginBottom: 7,
                    boxShadow: activeId === s.id ? `0 0 0 1px ${s.sc}` : "none",
                  }}>
                    <div onClick={() => focusStory(s.id)} style={{ cursor: "pointer" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                        {showBph && <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, fontWeight: 700, color: "#06101d", background: s.sc, borderRadius: 5, padding: "1px 6px" }}>#{s.sn}</span>}
                        {showBph && <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: s.sc, fontWeight: 600 }}>{s.id}</span>}
                      </div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink, margin: showBph ? "4px 0 3px" : "0 0 3px" }}>{s.title}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", marginTop: 3 }}>
                      <span onClick={() => focusStory(s.id)} title="Focus & play this story"
                        style={{ cursor: "pointer", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, color: activeId === s.id && !activeIface ? "#06101d" : s.sc, background: activeId === s.id && !activeIface ? s.sc : "transparent", border: `1px solid ${s.sc}`, borderRadius: 20, padding: "2px 8px" }}>
                        ▶ focus &amp; play
                      </span>
                      {ifaces.length > 0 && <span style={{ fontFamily: FONT_MONO, fontSize: 9, color: T.ink3 }}>·</span>}
                      {ifaces.map(fi => (
                        <span key={fi.id} onClick={(e) => { e.stopPropagation(); focusIface(fi.id, s.id); }}
                          onMouseEnter={e => setIfHover({ iface: fi, x: e.clientX, y: e.clientY })}
                          onMouseMove={e => setIfHover({ iface: fi, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setIfHover(null)}
                          title={`${fi.name} — ${fi.from} → ${fi.to}${fi.mid ? " via " + fi.mid : ""} · click to animate`}
                          style={{
                            cursor: "pointer", fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700,
                            color: flow.activeIface === fi.id ? "#06101d" : fi.color,
                            background: flow.activeIface === fi.id ? fi.color : fi.color + "1f",
                            border: `1px solid ${fi.color}`, borderRadius: 20, padding: "2px 7px",
                          }}>{fi.id.replace("IF-", "IF")}</span>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            );
          })}
          {!filtered.length && <div style={{ color: T.ink3, fontSize: 12, padding: 20, textAlign: "center" }}>No stories match.</div>}
        </div>
      </aside>
      )}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, position: "relative" }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${T.line}` }}>
          {toggles.map(([k, label, color, fn, on]) => (
            <Pill key={k} color={color} active={on} onClick={fn}>
              <span style={{ width: 9, height: 9, borderRadius: 9, background: on ? color : "#33465e", display: "inline-block" }} />{label}
            </Pill>
          ))}
          <div style={{ flex: 1 }} />
          <Btn color={T.b2mml} active={auto} onClick={() => auto ? clearFocus() : startAuto()}><Play size={13} />{auto ? "Stop auto" : "Auto-play all"}</Btn>
          <Btn onClick={() => setLayout({})}><RotateCcw size={13} />Reset layout</Btn>
          <Btn color={fullScreen ? T.plmsi : undefined} active={fullScreen} onClick={() => { setFullScreen(v => { const nv = !v; setSideOpen(!nv); setLegendOpen(!nv); return nv; }); }} title="Maximize the central diagram">
            {fullScreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}{fullScreen ? "Exit full screen" : "Full screen"}
          </Btn>
          <Btn onClick={clearFocus}><X size={13} />Clear</Btn>
        </div>

        <div style={{ flex: 1, position: "relative", minHeight: 0, overflow: "hidden" }}>
          <SwimLaneDiagram systems={systems} overlays={overlays} activeSteps={steps} seqOn={seqOn}
            tokenStep={token} dimSet={dimSet} layout={layout} onDragNode={(id, x, y) => setLayout(L => ({ ...L, [id]: { x, y } }))}
            accent={activeIface ? activeIface.color : null}
            onHoverNode={(s, e) => setHover(s ? { s, x: e.clientX, y: e.clientY } : null)} />

          {/* Interfaces & triggers for the focused story — in the main window */}
          {activeStory && (() => {
            const list = ifacesForStory(activeStory.id);
            if (!list.length) return null;
            return (
              <div style={{ position: "absolute", left: 12, top: 12, width: 268, maxHeight: "70%", overflowY: "auto", background: "rgba(8,15,26,.93)", border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 12px" }}>
                <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: ".12em", color: T.ink2, textTransform: "uppercase", marginBottom: 8 }}>
                  Interfaces &amp; triggers <span style={{ color: T.plmsi }}>· {showBph ? activeStory.id : activeStory.title}</span>
                </div>
                {list.map(f => (
                  <div key={f.id}
                    onMouseEnter={e => setIfHover({ iface: f, x: e.clientX, y: e.clientY })}
                    onMouseMove={e => setIfHover({ iface: f, x: e.clientX, y: e.clientY })}
                    onMouseLeave={() => setIfHover(null)}
                    onClick={() => focusIface(f.id, activeStory.id)}
                    style={{ border: `1px solid ${flow.activeIface === f.id ? f.color : T.line}`, borderLeft: `3px solid ${f.color}`, borderRadius: 9, padding: "7px 9px", marginBottom: 7, cursor: "pointer", background: flow.activeIface === f.id ? f.color + "1a" : T.panel }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, color: "#06101d", background: f.color, borderRadius: 4, padding: "1px 6px" }}>{f.id}</span>
                      <span style={{ fontSize: 11.5, fontWeight: 600, color: T.ink, lineHeight: 1.15 }}>{f.name}</span>
                    </div>
                    <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: T.ink3, marginTop: 3 }}>
                      {f.from} → {f.to}{f.mid ? ` · ${f.mid}` : ""} · {f.grp}
                    </div>
                    {f.trigger && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4 }}>
                        <span style={{ fontFamily: FONT_MONO, fontSize: 8.5, fontWeight: 700, color: T.gold, border: `1px solid ${T.gold}66`, borderRadius: 10, padding: "1px 6px" }}>⚡ {f.trigger.type || "trigger"}</span>
                        <span style={{ fontSize: 10, color: T.ink2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.trigger.name}{f.trigger.latency ? ` · ${f.trigger.latency}` : ""}</span>
                      </div>
                    )}
                    <div style={{ fontSize: 9, color: T.ink3, marginTop: 3, fontStyle: "italic" }}>hover for attributes exchanged →</div>
                  </div>
                ))}
              </div>
            );
          })()}
          {legendOpen ? (
          <div style={{ position: "absolute", right: 12, top: 12, background: "rgba(8,15,26,.9)", border: `1px solid ${T.line}`, borderRadius: 12, padding: "10px 12px", maxWidth: 196, maxHeight: "64%", overflowY: "auto" }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: ".12em", color: T.ink2, textTransform: "uppercase", marginBottom: 7, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span>● Business objects</span>
              <ChevronRight size={13} style={{ cursor: "pointer", color: T.ink3 }} onClick={() => setLegendOpen(false)} />
            </div>
            {BUSINESS_OBJECTS.map(b => (
              <div key={b.name} onClick={() => setBoFilter(boFilter === b.name ? null : b.name)} style={{
                display: "flex", alignItems: "center", gap: 7, fontSize: 10.5, padding: "3px 6px", borderRadius: 6, cursor: "pointer",
                color: boFilter === b.name ? T.ink : T.ink2, background: boFilter === b.name ? "rgba(255,255,255,.08)" : "transparent",
                border: `1px solid ${boFilter === b.name ? "rgba(255,255,255,.25)" : "transparent"}`, fontWeight: boFilter === b.name ? 600 : 400,
              }}>
                <span style={{ width: 11, height: 11, borderRadius: 3, background: b.color, flex: "none" }} />{b.name}
              </div>
            ))}
          </div>
          ) : (
            <button onClick={() => setLegendOpen(true)} title="Show business-object legend"
              style={{ position: "absolute", right: 12, top: 12, background: "rgba(8,15,26,.9)", border: `1px solid ${T.line}`, borderRadius: 10, padding: "6px 10px", color: T.ink2, cursor: "pointer", fontFamily: FONT_MONO, fontSize: 10, display: "flex", alignItems: "center", gap: 6 }}>
              <ChevronLeft size={13} />● BOs
            </button>
          )}
        </div>

        {focusObj && (
          <div style={dock === "float"
            ? { position: "absolute", left: playerPos.x, top: playerPos.y, width: "min(660px,84%)", background: "rgba(6,14,24,.97)", border: `1px solid ${activeIface ? activeIface.color : T.cpi}`, borderRadius: 14, padding: "11px 16px", boxShadow: "0 18px 50px rgba(0,0,0,.6)", zIndex: 30 }
            : { position: "absolute", left: "50%", bottom: 16, transform: "translateX(-50%)", width: "min(660px,84%)", background: "rgba(6,14,24,.95)", border: `1px solid ${activeIface ? activeIface.color : T.cpi}`, borderRadius: 14, padding: "11px 16px", boxShadow: "0 18px 50px rgba(0,0,0,.6)", zIndex: 30 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
              <span onPointerDown={onPlayerHandleDown} title="Drag to move (docks as floating)"
                style={{ cursor: "grab", color: T.ink3, fontFamily: FONT_MONO, fontSize: 9, letterSpacing: ".1em", display: "flex", alignItems: "center", gap: 6, userSelect: "none" }}>
                <GripHorizontal size={13} /> {dock === "float" ? "FLOATING — drag to move" : "DOCKED ↓"}
              </span>
              <span style={{ display: "flex", gap: 8 }}>
                {nav && activeIface && <span onClick={() => nav.goToInterface(activeIface.id)} title="Open this interface in the Tracker / LLD" style={{ cursor: "pointer", color: T.plmsi, fontFamily: FONT_MONO, fontSize: 9, display: "flex", alignItems: "center", gap: 4 }}><ExternalLink size={11} />open {activeIface.id}</span>}
                <span onClick={() => setDock(dock === "float" ? "bottom" : "float")} style={{ cursor: "pointer", color: T.ink3, fontFamily: FONT_MONO, fontSize: 9 }}>{dock === "float" ? "⤓ dock" : "⤢ float"}</span>
              </span>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 30, height: 30, borderRadius: 15, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 14, color: "#04121f", background: stepColor, flex: "none" }}>{step + 1}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                {activeIface ? (
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                    <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: "#06101d", background: activeIface.color, borderRadius: 5, padding: "1px 6px" }}>{activeIface.id}</span>{" "}
                    <span style={{ color: activeIface.color }}>{activeIface.name}</span>{" "}
                    <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: T.ink3 }}>· {activeIface.grp} · {activeIface.type}{activeStory ? ` · in ${activeStory.id}` : ""}</span>
                  </div>
                ) : (
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: T.ink }}>
                    {showBph && <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: "#06101d", background: activeStory.sc, borderRadius: 5, padding: "1px 6px" }}>#{activeStory.sn}</span>}{showBph ? " " : ""}
                    {showBph && <span style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: activeStory.sc }}>{activeStory.id}</span>}
                  </div>
                )}
                <div style={{ fontSize: 11, color: T.ink2, marginTop: 2 }}>
                  <span style={{ color: stepColor }}>{curStep ? curStep.bo : ""}</span>{curStep ? ` · ${curStep.f} → ${curStep.t}` : ""}
                  {activeIface && curStep ? ` — ${activeIface.boFull}` : (curStep ? ` — ${curStep.note}` : "")}
                </div>
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                {[[SkipBack, () => { setAuto(false); setPlaying(false); const n = Math.max(0, step - 1); setStep(n); runToken(n); }],
                [playing ? Pause : Play, () => setPlaying(p => !p)],
                [SkipForward, () => { setAuto(false); setPlaying(false); const n = Math.min(steps.length - 1, step + 1); setStep(n); runToken(n); }],
                [Square, clearFocus]].map(([Ic, fn], i) => (
                  <button key={i} onClick={fn} style={{ width: 30, height: 30, borderRadius: 8, border: `1px solid ${T.line}`, background: T.panel, color: T.ink, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}><Ic size={14} /></button>
                ))}
              </div>
            </div>
            <div style={{ height: 3, background: "#16273c", borderRadius: 2, marginTop: 9, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${((step + 1) / Math.max(1, steps.length)) * 100}%`, background: `linear-gradient(90deg,${T.plmsi},${activeIface ? activeIface.color : T.cpi})`, transition: "width .3s" }} />
            </div>
          </div>
        )}

        {ifHover && (
          <div style={{ position: "fixed", left: Math.min(ifHover.x + 16, (typeof window !== "undefined" ? window.innerWidth : 1200) - 320), top: ifHover.y + 14, width: 300, background: "#06101d", border: `1px solid ${ifHover.iface.color}`, borderRadius: 11, padding: "10px 13px", zIndex: 70, pointerEvents: "none", boxShadow: "0 16px 40px rgba(0,0,0,.6)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5 }}>
              <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, color: "#06101d", background: ifHover.iface.color, borderRadius: 4, padding: "1px 6px" }}>{ifHover.iface.id}</span>
              <span style={{ fontSize: 11.5, fontWeight: 700, color: T.ink }}>{ifHover.iface.name}</span>
            </div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: T.ink3, marginBottom: 6 }}>{ifHover.iface.boFull} · {ifHover.iface.type}</div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 8.5, letterSpacing: ".1em", color: T.ink2, textTransform: "uppercase", marginBottom: 4 }}>Attributes exchanged at this interface</div>
            {(ifHover.iface.attrs || []).map((a, i) => (
              <div key={i} style={{ fontFamily: FONT_MONO, fontSize: 10, color: T.ink, lineHeight: 1.5, borderLeft: `2px solid ${ifHover.iface.color}`, paddingLeft: 7, marginBottom: 2 }}>{a}</div>
            ))}
          </div>
        )}

        {hover && (
          <div style={{ position: "fixed", left: Math.min(hover.x + 15, (typeof window !== "undefined" ? window.innerWidth : 1200) - 280), top: hover.y + 15, background: "#06101d", border: `1px solid ${T.plmsi}`, borderRadius: 10, padding: "9px 12px", fontSize: 11.5, maxWidth: 260, color: T.ink, zIndex: 60, pointerEvents: "none" }}>
            <b style={{ color: T.plmsi, fontFamily: FONT_MONO, fontSize: 10.5 }}>{hover.s.name}</b> · {hover.s.sub}
            <div style={{ marginTop: 4 }}>{hover.s.desc}</div>
            <div style={{ marginTop: 4, color: T.ink3, fontFamily: FONT_MONO, fontSize: 10 }}>{hover.s.vendor} · ISA-95 {hover.s.level} · {hover.s.channel.toUpperCase()}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   SETUP — graphical configurator: prepare a new CLM setup by clicking only
   Steps: 1) pick systems on the canvas  2) pick in-scope BPH stories
          3) set maturity target  4) review & generate config
   ============================================================ */
function Setup({ systems, scope, setScope, maturity, setMaturity, goFlow }) {
  const [stepIdx, setStepIdx] = useState(0);
  const [selSys, setSelSys] = useState(() => scope.systems && scope.systems.size ? new Set(scope.systems) : new Set(systems.map(s => s.id)));
  const [selStories, setSelStories] = useState(() => scope.stories && scope.stories.size ? new Set(scope.stories) : new Set());
  const [famSel, setFamSel] = useState("ALL");
  const [hover, setHover] = useState(null);

  const STEPS = ["Select systems", "Select user stories", "Target maturity", "Review & generate"];

  const toggleSys = (id) => setSelSys(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleStory = (id) => setSelStories(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // stories available given selected systems (all their nodes must be selected)
  const storiesForSel = useMemo(() => STORIES.filter(s => s.steps.every(st => selSys.has(st.f) && selSys.has(st.t))), [selSys]);
  const visibleStories = useMemo(() => storiesForSel.filter(s => famSel === "ALL" || s.fam === famSel), [storiesForSel, famSel]);

  function quickPick(kind) {
    if (kind === "all") setSelStories(new Set(storiesForSel.map(s => s.id)));
    else if (kind === "none") setSelStories(new Set());
    else setSelStories(new Set(storiesForSel.filter(s => s.fam === kind).map(s => s.id)));
  }
  function generate() {
    setScope({ systems: new Set(selSys), stories: new Set(selStories) });
    goFlow();
  }

  const inScopeSystems = systems.filter(s => selSys.has(s.id));
  const Stepper = () => (
    <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
      {STEPS.map((s, i) => (
        <div key={i} onClick={() => setStepIdx(i)} style={{
          display: "flex", alignItems: "center", gap: 8, padding: "8px 13px", borderRadius: 10, cursor: "pointer",
          background: i === stepIdx ? "linear-gradient(180deg,rgba(34,211,197,.16),transparent)" : T.panel,
          border: `1px solid ${i === stepIdx ? T.plmsi : i < stepIdx ? T.b2mml + "66" : T.line}`,
        }}>
          <div style={{ width: 22, height: 22, borderRadius: 11, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 12, color: "#06101d", background: i < stepIdx ? T.b2mml : i === stepIdx ? T.plmsi : "#33465e" }}>
            {i < stepIdx ? "✓" : i + 1}
          </div>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: i === stepIdx ? T.ink : T.ink2 }}>{s}</span>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0, padding: "16px 20px 0" }}>
      <SectionTitle icon={<Wand2 size={20} color={T.b2mml} />} title="Setup configurator — build a new CLM setup, graphically"
        sub="Prepare a brand-new landscape with point-and-click only. Toggle the systems you have, pick the BPH user stories in scope, choose a maturity target, and generate a scoped configuration that drives the whole studio. No data files to edit." />
      <Stepper />

      <div style={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex", flexDirection: "column" }}>
        {/* STEP 1 — systems on canvas */}
        {stepIdx === 0 && (
          <>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
              <Pill color={T.plmsi} active>{selSys.size}/{systems.length} systems selected</Pill>
              <Btn onClick={() => setSelSys(new Set(systems.map(s => s.id)))}>Select all</Btn>
              <Btn onClick={() => setSelSys(new Set())}>Clear</Btn>
              <span style={{ fontSize: 11.5, color: T.ink3 }}>Click any node on the diagram to include / exclude it.</span>
              <div style={{ flex: 1 }} />
              <Btn color={T.plmsi} active onClick={() => setStepIdx(1)}>Next: stories <ChevronRight size={14} /></Btn>
            </div>
            <div style={{ flex: 1, minHeight: 0, position: "relative", border: `1px solid ${T.line}`, borderRadius: 12, overflow: "hidden" }}>
              <SwimLaneDiagram systems={systems} overlays={{ plmsi: true, cpi: true, boundary: true }} activeSteps={[]} seqOn={false}
                tokenStep={null} dimSet={null} selectable selectedSet={selSys} onToggleNode={toggleSys}
                onHoverNode={(s, e) => setHover(s ? { s, x: e.clientX, y: e.clientY } : null)} />
              {hover && (
                <div style={{ position: "fixed", left: Math.min(hover.x + 15, (typeof window !== "undefined" ? window.innerWidth : 1200) - 280), top: hover.y + 15, background: "#06101d", border: `1px solid ${T.plmsi}`, borderRadius: 10, padding: "9px 12px", fontSize: 11.5, maxWidth: 260, color: T.ink, zIndex: 60, pointerEvents: "none" }}>
                  <b style={{ color: T.plmsi, fontFamily: FONT_MONO, fontSize: 10.5 }}>{hover.s.name}</b> · {hover.s.sub}
                  <div style={{ marginTop: 4 }}>{hover.s.desc}</div>
                </div>
              )}
            </div>
          </>
        )}

        {/* STEP 2 — stories grid */}
        {stepIdx === 1 && (
          <>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 10, flexWrap: "wrap" }}>
              <Pill color={T.b2mml} active>{selStories.size} stories in scope</Pill>
              <span style={{ fontSize: 11.5, color: T.ink3 }}>{storiesForSel.length} available with the selected systems</span>
              <div style={{ display: "flex", gap: 5, marginLeft: 8 }}>
                {["ALL", "T", "O", "K"].map(f => (
                  <button key={f} onClick={() => setFamSel(f)} style={{ padding: "5px 11px", borderRadius: 7, fontFamily: FONT_MONO, fontSize: 10.5, fontWeight: 600, cursor: "pointer", color: famSel === f ? "#06101d" : T.ink2, background: famSel === f ? (f === "ALL" ? "#9fb6cd" : FAM_META[f].color) : T.panel, border: `1px solid ${famSel === f ? "transparent" : T.line}` }}>{f === "ALL" ? "All" : "BPH." + f}</button>
                ))}
              </div>
              <div style={{ flex: 1 }} />
              <Btn onClick={() => quickPick("all")}>+ All available</Btn>
              <Btn onClick={() => quickPick("none")}>Clear</Btn>
              <Btn color={T.plmsi} active onClick={() => setStepIdx(2)}>Next <ChevronRight size={14} /></Btn>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto", border: `1px solid ${T.line}`, borderRadius: 12, padding: 12 }}>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(250px,1fr))", gap: 8 }}>
                {visibleStories.map(s => {
                  const on = selStories.has(s.id);
                  return (
                    <div key={s.id} onClick={() => toggleStory(s.id)} style={{
                      display: "flex", gap: 9, alignItems: "flex-start", padding: "9px 11px", borderRadius: 9, cursor: "pointer",
                      background: on ? "rgba(91,229,132,.08)" : T.panel, border: `1px solid ${on ? T.b2mml : T.line}`, borderLeft: `3px solid ${s.sc}`,
                    }}>
                      {on ? <CheckCircle2 size={16} color={T.b2mml} style={{ flex: "none", marginTop: 1 }} /> : <Circle size={16} color={T.ink3} style={{ flex: "none", marginTop: 1 }} />}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ fontFamily: FONT_MONO, fontSize: 9, fontWeight: 700, color: "#06101d", background: s.sc, borderRadius: 4, padding: "1px 5px" }}>#{s.sn}</span>
                          <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: s.sc }}>{s.id}</span>
                        </div>
                        <div style={{ fontSize: 12, color: T.ink, fontWeight: 600, marginTop: 3 }}>{s.title}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              {!visibleStories.length && <div style={{ color: T.ink3, fontSize: 12, padding: 30, textAlign: "center" }}>No stories available — go back and select more systems.</div>}
            </div>
          </>
        )}

        {/* STEP 3 — maturity */}
        {stepIdx === 2 && (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }} />
              <Btn color={T.plmsi} active onClick={() => setStepIdx(3)}>Next: review <ChevronRight size={14} /></Btn>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
              {MATURITY.map(m => {
                const active = m.lvl === maturity, below = m.lvl < maturity;
                return (
                  <div key={m.lvl} onClick={() => setMaturity(m.lvl)} style={{ display: "flex", gap: 16, padding: "13px 18px", borderRadius: 12, cursor: "pointer", border: `1px solid ${active ? m.color : T.line}`, background: active ? `linear-gradient(100deg,${m.color}18,transparent)` : T.panel, boxShadow: active ? `0 0 0 1px ${m.color}` : "none" }}>
                    <div style={{ width: 54, flex: "none", textAlign: "center" }}>
                      <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 28, color: below || active ? m.color : T.ink3 }}>L{m.lvl}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 14.5, fontWeight: 700, color: T.ink }}>{m.name}</div>
                      <div style={{ fontSize: 12, color: T.ink2, marginTop: 2 }}>{m.desc}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* STEP 4 — review */}
        {stepIdx === 3 && (
          <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 12, marginBottom: 14 }}>
              {[["Systems in scope", selSys.size, T.blue], ["User stories in scope", selStories.size, T.cpi], ["Target maturity", "L" + maturity, MATURITY[maturity].color], ["Distinct business objects", new Set(STORIES.filter(s => selStories.has(s.id)).flatMap(s => s.steps.map(x => x.bo))).size, T.violet]].map(([l, v, c]) => (
                <Panel key={l} style={{ padding: 16, borderLeft: `4px solid ${c}` }}>
                  <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 30, color: T.ink }}>{v}</div>
                  <div style={{ fontSize: 12, color: T.ink2, fontWeight: 600 }}>{l}</div>
                </Panel>
              ))}
            </div>
            <Panel style={{ padding: 16, marginBottom: 14 }}>
              <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: ".12em", color: T.ink2, textTransform: "uppercase", marginBottom: 10 }}>In-scope systems by ISA-95 level</div>
              {LANES.map(l => {
                const items = inScopeSystems.filter(s => s.level === l.id); if (!items.length) return null;
                return (
                  <div key={l.id} style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 6 }}>
                    <span style={{ width: 30, fontFamily: FONT_DISPLAY, fontWeight: 800, color: LANE_COLOR[l.id] }}>{l.id}</span>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>{items.map(s => <Pill key={s.id} color={s.color}>{s.name}</Pill>)}</div>
                  </div>
                );
              })}
            </Panel>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn color={T.b2mml} active onClick={generate}><Wand2 size={14} />Generate config & open Flow Studio</Btn>
              <Btn onClick={() => { setScope({ systems: new Set(selSys), stories: new Set(selStories) }); }}><Check size={14} />Save scope</Btn>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   DASHBOARD
   ============================================================ */
function Dashboard({ systems, maturity, setTab, scope }) {
  const counts = useMemo(() => {
    const byLevel = {}; LANES.forEach(l => byLevel[l.id] = 0);
    systems.forEach(s => byLevel[s.level]++);
    return { byLevel, plmsi: systems.filter(s => s.channel === "plmsi").length, cpi: systems.filter(s => s.channel === "cpi").length, custom: systems.filter(s => s.custom).length };
  }, [systems]);
  const m = MATURITY[maturity];
  const scoped = scope && scope.stories ? scope.stories.size : 0;
  const kpis = [
    { label: "Systems", val: systems.length, sub: `${counts.custom} custom`, color: T.blue, icon: <Server size={18} /> },
    { label: "Business objects", val: BUSINESS_OBJECTS.length, sub: "across the boundary", color: T.violet, icon: <Boxes size={18} /> },
    { label: "BPH user stories", val: STORY_COUNT, sub: scoped ? `${scoped} in scope` : "T · O · K", color: T.cpi, icon: <Workflow size={18} /> },
    { label: "Impl. artifacts", val: ARTIFACT_TOTAL, sub: `${PHASES.length} phases`, color: T.b2mml, icon: <ListChecks size={18} /> },
  ];
  return (
    <div style={{ padding: "20px 24px 40px", overflowY: "auto", height: "100%" }}>
      <SectionTitle icon={<LayoutDashboard size={20} color={T.plmsi} />} title="CLM Studio — control room"
        sub="Architect and implement end-to-end closed-loop manufacturing on Siemens software, with PLMSI and SAP CPI co-existing at a governed boundary — extensible to any ISA-95 system. Start in Setup to scope a new landscape graphically." />
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))", gap: 14, marginBottom: 18 }}>
        {kpis.map(k => (
          <Panel key={k.label} style={{ padding: 16, position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 4, background: k.color }} />
            <div style={{ color: k.color }}>{k.icon}</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 34, color: T.ink, marginTop: 8 }}>{k.val}</div>
            <div style={{ fontSize: 12, color: T.ink, fontWeight: 600 }}>{k.label}</div>
            <div style={{ fontSize: 11, color: T.ink3, fontFamily: FONT_MONO }}>{k.sub}</div>
          </Panel>
        ))}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1.3fr 1fr", gap: 14 }}>
        <Panel style={{ padding: 18 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: ".12em", color: T.ink2, textTransform: "uppercase", marginBottom: 12 }}>ISA-95 system distribution</div>
          {LANES.map(l => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 10 }}>
              <div style={{ width: 30, fontFamily: FONT_DISPLAY, fontWeight: 800, color: LANE_COLOR[l.id] }}>{l.id}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: T.ink, fontWeight: 600 }}>{l.label}</div>
                <div style={{ height: 8, background: "#13243a", borderRadius: 5, marginTop: 4, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${Math.min(100, (counts.byLevel[l.id] / Math.max(1, systems.length)) * 100 * 2.2)}%`, background: LANE_COLOR[l.id], opacity: 0.8 }} />
                </div>
              </div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 13, color: T.ink, width: 24, textAlign: "right" }}>{counts.byLevel[l.id]}</div>
            </div>
          ))}
          <div style={{ display: "flex", gap: 18, marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.line}` }}>
            <div><span style={{ color: T.plmsi, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22 }}>{counts.plmsi}</span> <span style={{ color: T.ink2, fontSize: 12 }}>PLMSI</span></div>
            <div><span style={{ color: T.cpi, fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 22 }}>{counts.cpi}</span> <span style={{ color: T.ink2, fontSize: 12 }}>CPI</span></div>
            <div style={{ flex: 1 }} />
            <Btn color={T.b2mml} active onClick={() => setTab("setup")}><Wand2 size={13} />New setup</Btn>
          </div>
        </Panel>
        <Panel style={{ padding: 18 }}>
          <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: ".12em", color: T.ink2, textTransform: "uppercase", marginBottom: 12 }}>CLM maturity</div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 46, color: m.color }}>L{m.lvl}</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>{m.name}</div>
          </div>
          <p style={{ fontSize: 12.5, color: T.ink2, lineHeight: 1.5, marginTop: 6 }}>{m.desc}</p>
          <div style={{ display: "flex", gap: 5, marginTop: 12 }}>{MATURITY.map(x => <div key={x.lvl} style={{ flex: 1, height: 6, borderRadius: 3, background: x.lvl <= m.lvl ? x.color : "#1b2c44" }} />)}</div>
          <Btn color={T.gold} style={{ marginTop: 14 }} onClick={() => setTab("maturity")}><Gauge size={13} />Maturity assessment</Btn>
        </Panel>
      </div>
      <Panel style={{ padding: 18, marginTop: 14 }}>
        <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: ".12em", color: T.ink2, textTransform: "uppercase", marginBottom: 12 }}>Implementation roadmap</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {PHASES.map(p => (
            <div key={p.id} onClick={() => setTab("impl")} style={{ flex: "1 1 130px", minWidth: 130, background: T.panel2, border: `1px solid ${p.color}44`, borderTop: `3px solid ${p.color}`, borderRadius: 10, padding: "10px 12px", cursor: "pointer" }}>
              <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, color: p.color, fontSize: 14 }}>{p.icon} {p.id}</div>
              <div style={{ fontSize: 11.5, color: T.ink, fontWeight: 600, marginTop: 3 }}>{p.name}</div>
              <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, color: T.ink3, marginTop: 4 }}>{p.dur} · {p.art} artifacts</div>
            </div>
          ))}
        </div>
      </Panel>
    </div>
  );
}

/* ============================================================
   ARCHITECTURE
   ============================================================ */
function Architecture({ systems, layout, setLayout }) {
  const [overlays, setOverlays] = useState({ plmsi: true, cpi: true, boundary: true, maturity: false });
  const [hover, setHover] = useState(null);
  const toggles = [["plmsi", "PLMSI channel", T.plmsi], ["cpi", "CPI channel", T.cpi], ["boundary", "Demarcation", T.boundary], ["maturity", "Maturity bands", T.gold]];
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
      <div style={{ padding: "16px 24px 0" }}>
        <SectionTitle icon={<Network size={20} color={T.plmsi} />} title="ISA-95 reference architecture"
          sub="The full closed-loop landscape across all five ISA-95 levels. PLMSI owns the Siemens side, CPI owns the SAP side, meeting at the demarcation line. Hover any system for its role and owner." />
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {toggles.map(([k, label, color]) => (
            <Pill key={k} color={color} active={overlays[k]} onClick={() => setOverlays(o => ({ ...o, [k]: !o[k] }))}>
              <span style={{ width: 9, height: 9, borderRadius: 9, background: overlays[k] ? color : "#33465e", display: "inline-block" }} />{label}
            </Pill>
          ))}
          <Btn onClick={() => setLayout({})} style={{ marginLeft: 4 }}><RotateCcw size={13} />Reset layout</Btn>
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, padding: "0 16px 16px", position: "relative" }}>
        <SwimLaneDiagram systems={systems} overlays={overlays} activeSteps={[]} seqOn={false} tokenStep={null} dimSet={null}
          layout={layout} onDragNode={(id, x, y) => setLayout(L => ({ ...L, [id]: { x, y } }))}
          onHoverNode={(s, e) => setHover(s ? { s, x: e.clientX, y: e.clientY } : null)} />
        {hover && (
          <div style={{ position: "fixed", left: Math.min(hover.x + 15, (typeof window !== "undefined" ? window.innerWidth : 1200) - 280), top: hover.y + 15, background: "#06101d", border: `1px solid ${T.plmsi}`, borderRadius: 10, padding: "9px 12px", fontSize: 11.5, maxWidth: 260, color: T.ink, zIndex: 60, pointerEvents: "none" }}>
            <b style={{ color: T.plmsi, fontFamily: FONT_MONO, fontSize: 10.5 }}>{hover.s.name}</b> · {hover.s.sub}
            <div style={{ marginTop: 4 }}>{hover.s.desc}</div>
            <div style={{ marginTop: 4, color: T.ink3, fontFamily: FONT_MONO, fontSize: 10 }}>{hover.s.vendor} · ISA-95 {hover.s.level} · {hover.s.channel.toUpperCase()}</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================
   SYSTEMS CATALOG  (+ add custom ISA-95 system)
   ============================================================ */
function SystemsCatalog({ systems, setSystems }) {
  const [form, setForm] = useState({ name: "", vendor: "", level: "L3", role: "app", channel: "none", sub: "", desc: "" });
  const [adding, setAdding] = useState(false);
  function addSystem() {
    if (!form.name.trim()) return;
    const id = form.name.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8) + "_" + Math.random().toString(36).slice(2, 5);
    const x = GUT + 120 + (systems.filter(s => s.level === form.level).length % 6) * 200;
    const palette = ["#7dd3fc", "#fca5a5", "#bef264", "#f0abfc", "#fdba74", "#a5f3fc", "#c4b5fd"];
    setSystems([...systems, { ...form, id, x, color: palette[systems.length % palette.length], custom: true }]);
    setForm({ name: "", vendor: "", level: "L3", role: "app", channel: "none", sub: "", desc: "" }); setAdding(false);
  }
  return (
    <div style={{ padding: "20px 24px 40px", overflowY: "auto", height: "100%" }}>
      <SectionTitle icon={<Package size={20} color={T.plmsi} />} title="Systems catalog"
        sub="The Siemens-based reference systems by ISA-95 level and channel. Add any custom product — it slots into the architecture, the Setup configurator and Flow Studio automatically, by declaring its ISA-95 level and role." />
      <div style={{ marginBottom: 14 }}>
        {!adding
          ? <Btn color={T.b2mml} active onClick={() => setAdding(true)}><Plus size={14} />Add custom system (ISA-95)</Btn>
          : <Panel style={{ padding: 16 }}>
            <div style={{ fontFamily: FONT_MONO, fontSize: 11, color: T.b2mml, marginBottom: 10 }}>NEW ISA-95 SYSTEM</div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(160px,1fr))", gap: 10 }}>
              {[["name", "Name *"], ["vendor", "Vendor"], ["sub", "Subtitle"]].map(([k, ph]) => (
                <input key={k} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} placeholder={ph}
                  style={{ background: T.bg2, border: `1px solid ${T.line}`, borderRadius: 8, color: T.ink, padding: "8px 11px", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }} />
              ))}
              {[["level", LANES.map(l => l.id)], ["role", ROLES], ["channel", CHANNELS]].map(([k, opts]) => (
                <select key={k} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })}
                  style={{ background: T.bg2, border: `1px solid ${T.line}`, borderRadius: 8, color: T.ink, padding: "8px 11px", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }}>
                  {opts.map(o => <option key={o} value={o}>{k}: {o}</option>)}
                </select>
              ))}
              <input value={form.desc} onChange={e => setForm({ ...form, desc: e.target.value })} placeholder="Description"
                style={{ gridColumn: "1/-1", background: T.bg2, border: `1px solid ${T.line}`, borderRadius: 8, color: T.ink, padding: "8px 11px", fontSize: 12, fontFamily: FONT_BODY, outline: "none" }} />
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <Btn color={T.b2mml} active onClick={addSystem}><Check size={14} />Add to landscape</Btn>
              <Btn onClick={() => setAdding(false)}>Cancel</Btn>
            </div>
          </Panel>}
      </div>
      {LANES.map(l => {
        const items = systems.filter(s => s.level === l.id); if (!items.length) return null;
        return (
          <div key={l.id} style={{ marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, color: LANE_COLOR[l.id], fontSize: 18 }}>{l.id}</span>
              <span style={{ fontSize: 13, color: T.ink, fontWeight: 600 }}>{l.label}</span>
              <span style={{ fontFamily: FONT_MONO, fontSize: 10, color: T.ink3 }}>{l.iso}</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: 10 }}>
              {items.map(s => (
                <Panel key={s.id} style={{ padding: 13, borderLeft: `3px solid ${s.color}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                      <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>{s.name}{s.custom && <span style={{ marginLeft: 6, fontSize: 9, color: T.b2mml, fontFamily: FONT_MONO }}>CUSTOM</span>}</div>
                      <div style={{ fontSize: 11, color: T.ink2 }}>{s.sub}</div>
                    </div>
                    {s.custom && <Trash2 size={14} color={T.ink3} style={{ cursor: "pointer" }} onClick={() => setSystems(systems.filter(x => x.id !== s.id))} />}
                  </div>
                  <div style={{ fontSize: 11, color: T.ink2, marginTop: 7, lineHeight: 1.45 }}>{s.desc}</div>
                  <div style={{ display: "flex", gap: 6, marginTop: 9, flexWrap: "wrap" }}>
                    <Pill color={T.ink2}>{s.vendor}</Pill>
                    <Pill color={s.role === "gateway" || s.role === "bus" ? T.cpi : T.blue}>{s.role}</Pill>
                    <Pill color={s.channel === "plmsi" ? T.plmsi : s.channel === "cpi" ? T.cpi : T.ink3}>{s.channel}</Pill>
                  </div>
                </Panel>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   BUSINESS OBJECTS
   ============================================================ */
function BusinessObjectsTab() {
  return (
    <div style={{ padding: "20px 24px 40px", overflowY: "auto", height: "100%" }}>
      <SectionTitle icon={<Boxes size={20} color={T.violet} />} title="Business-object register"
        sub="Every business object that flows across the landscape — its system of record, the SAP-side artifact (iDoc/BAPI), the PLMSI channel that carries it, and the direction. The canonical/B2MML contract is built from these." />
      <Panel style={{ overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead><tr>{["Business Object", "Direction", "System of Record", "SAP-side artifact", "PLMSI channel"].map(h => (
            <th key={h} style={{ background: "#10243a", color: "#cfe2f5", fontFamily: FONT_MONO, fontSize: 10.5, letterSpacing: ".05em", textTransform: "uppercase", padding: "11px 13px", textAlign: "left", borderBottom: `1px solid ${T.line}`, position: "sticky", top: 0 }}>{h}</th>
          ))}</tr></thead>
          <tbody>
            {BUSINESS_OBJECTS.map((b, i) => (
              <tr key={b.name} style={{ background: i % 2 ? "rgba(255,255,255,.015)" : "transparent" }}>
                <td style={{ padding: "10px 13px", borderBottom: `1px solid ${T.line}` }}>
                  <span style={{ display: "inline-block", width: 12, height: 12, borderRadius: 3, background: b.color, marginRight: 8, verticalAlign: "-1px" }} /><b style={{ color: T.ink }}>{b.name}</b>
                </td>
                <td style={{ padding: "10px 13px", borderBottom: `1px solid ${T.line}`, color: T.ink2 }}>{b.dir}</td>
                <td style={{ padding: "10px 13px", borderBottom: `1px solid ${T.line}`, color: T.ink2 }}>{b.sor}</td>
                <td style={{ padding: "10px 13px", borderBottom: `1px solid ${T.line}`, color: T.ink2, fontFamily: FONT_MONO, fontSize: 11.5 }}>{b.sap}</td>
                <td style={{ padding: "10px 13px", borderBottom: `1px solid ${T.line}`, color: T.plmsi, fontFamily: FONT_MONO, fontSize: 11.5 }}>{b.channel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

/* ============================================================
   IMPLEMENTATION (runbook / WBS)
   ============================================================ */
function Implementation() {
  const [done, setDone] = useState({});
  const [open, setOpen] = useState({ P0: true });
  const total = PHASES.reduce((a, p) => a + p.dod.length, 0);
  const doneCount = Object.values(done).filter(Boolean).length;
  const pct = Math.round((doneCount / total) * 100);
  return (
    <div style={{ padding: "20px 24px 40px", overflowY: "auto", height: "100%" }}>
      <SectionTitle icon={<ListChecks size={20} color={T.b2mml} />} title="Implementation runbook & WBS"
        sub="The 7-phase path to a live, governed CN4S/AIG ↔ CPI integration. Tick the Definition-of-Done criteria to track readiness. The canonical contract (P1) gates all build." />
      <Panel style={{ padding: "14px 18px", marginBottom: 16, display: "flex", alignItems: "center", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 12, color: T.ink2, marginBottom: 6 }}>Overall readiness — {doneCount}/{total} DoD criteria</div>
          <div style={{ height: 10, background: "#13243a", borderRadius: 6, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: `linear-gradient(90deg,${T.plmsi},${T.cpi})`, transition: "width .4s" }} />
          </div>
        </div>
        <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 28, color: T.ink }}>{pct}%</div>
      </Panel>
      {PHASES.map(p => {
        const isOpen = open[p.id]; const pd = p.dod.filter((_, i) => done[`${p.id}-${i}`]).length;
        return (
          <div key={p.id} style={{ marginBottom: 12 }}>
            <div onClick={() => setOpen(o => ({ ...o, [p.id]: !o[p.id] }))} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 15px", borderRadius: isOpen ? "12px 12px 0 0" : 12, border: `1px solid ${p.color}55`, background: `linear-gradient(100deg,${p.color}18,transparent)`, cursor: "pointer" }}>
              {isOpen ? <ChevronDown size={16} color={p.color} /> : <ChevronRight size={16} color={p.color} />}
              <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 15, color: p.color, background: "#06101d", border: `1px solid ${p.color}`, borderRadius: 8, padding: "2px 9px" }}>{p.id}</span>
              <span style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 15, color: T.ink }}>{p.icon} {p.name}</span>
              <div style={{ flex: 1 }} />
              <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: T.ink2, textAlign: "right" }}>{p.owner}<br />⏱ {p.dur} · {pd}/{p.dod.length} done · {p.art} artifacts</span>
            </div>
            {isOpen && (
              <div style={{ border: `1px solid ${p.color}55`, borderTop: "none", borderRadius: "0 0 12px 12px", padding: "12px 15px", background: T.panel }}>
                <div style={{ fontSize: 12, color: T.ink2, fontStyle: "italic", marginBottom: 10 }}>{p.goal}</div>
                {p.dod.map((d, i) => {
                  const key = `${p.id}-${i}`; const dn = done[key];
                  return (
                    <div key={i} onClick={() => setDone(s => ({ ...s, [key]: !s[key] }))} style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", cursor: "pointer", borderBottom: i < p.dod.length - 1 ? `1px solid ${T.line}` : "none" }}>
                      {dn ? <Check size={17} color={T.b2mml} /> : <Circle size={17} color={T.ink3} />}
                      <span style={{ fontSize: 12.5, color: dn ? T.ink3 : T.ink, textDecoration: dn ? "line-through" : "none" }}>{d}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ============================================================
   MATURITY
   ============================================================ */
function MaturityTab({ maturity, setMaturity }) {
  return (
    <div style={{ padding: "20px 24px 40px", overflowY: "auto", height: "100%" }}>
      <SectionTitle icon={<Gauge size={20} color={T.gold} />} title="CLM maturity model"
        sub="Locate the organisation on the closed-loop maturity ladder (L0–L5). The target architecture and which capabilities to light up depend on where you are and where you're going." />
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {MATURITY.map(m => {
          const active = m.lvl === maturity, below = m.lvl < maturity;
          return (
            <div key={m.lvl} onClick={() => setMaturity(m.lvl)} style={{ display: "flex", gap: 16, padding: "14px 18px", borderRadius: 12, cursor: "pointer", border: `1px solid ${active ? m.color : T.line}`, background: active ? `linear-gradient(100deg,${m.color}18,transparent)` : T.panel, boxShadow: active ? `0 0 0 1px ${m.color}` : "none" }}>
              <div style={{ width: 60, flex: "none", textAlign: "center" }}>
                <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 32, color: below || active ? m.color : T.ink3 }}>L{m.lvl}</div>
                {(below || active) && <Check size={14} color={m.color} />}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: T.ink }}>{m.name}</div>
                <div style={{ fontSize: 12.5, color: T.ink2, marginTop: 3, lineHeight: 1.5 }}>{m.desc}</div>
                <div style={{ display: "flex", gap: 7, marginTop: 8, flexWrap: "wrap" }}>{m.caps.map(c => <Pill key={c} color={m.color}>{c}</Pill>)}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================
   EXPORT DIALOG — choose format, captures the exact live scenario
   ============================================================ */
function ExportDialog({ onClose, getState }) {
  const [title, setTitle] = useState("CLM Scenario");
  const [fmt, setFmt] = useState("interactive");
  const opts = [
    { id: "interactive", icon: <FileCode size={18} />, label: "Interactive HTML", desc: "Self-contained Flow Studio — draggable nodes, story player, CPI toggle, BO filter, auto-play. Matches the live studio." },
    { id: "static", icon: <FileText size={18} />, label: "Static HTML", desc: "Non-interactive snapshot: the diagram plus systems, stories, business-object and runbook tables. Great for wikis." },
    { id: "pdf", icon: <FileDown size={18} />, label: "PDF", desc: "Opens the static snapshot in a print window — choose “Save as PDF”. A3 landscape, print-optimised." },
  ];
  function run() { exportScenario(fmt, { ...getState(), title: title || "CLM Scenario" }); onClose(); }
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(3,7,12,.7)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div onClick={e => e.stopPropagation()} style={{ width: "min(560px,92%)", background: T.panel, border: `1px solid ${T.line2}`, borderRadius: 16, padding: 22, boxShadow: "0 30px 80px rgba(0,0,0,.6)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
          <Download size={20} color={T.b2mml} />
          <h2 style={{ fontFamily: FONT_DISPLAY, fontWeight: 700, fontSize: 18, color: T.ink, margin: 0 }}>Export scenario</h2>
          <div style={{ flex: 1 }} />
          <X size={18} color={T.ink3} style={{ cursor: "pointer" }} onClick={onClose} />
        </div>
        <p style={{ color: T.ink2, fontSize: 12.5, marginBottom: 14 }}>Captures exactly what you see now — in-scope systems, dragged positions, the selected story, CPI mode, overlays and maturity.</p>
        <div style={{ marginBottom: 6, fontSize: 11.5, color: T.ink2, fontFamily: FONT_MONO }}>TITLE</div>
        <input value={title} onChange={e => setTitle(e.target.value)} style={{ width: "100%", background: T.bg2, border: `1px solid ${T.line}`, borderRadius: 9, color: T.ink, padding: "9px 12px", fontSize: 13, fontFamily: FONT_BODY, outline: "none", marginBottom: 14 }} />
        <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
          {opts.map(o => (
            <div key={o.id} onClick={() => setFmt(o.id)} style={{ display: "flex", gap: 12, padding: "12px 14px", borderRadius: 11, cursor: "pointer", border: `1px solid ${fmt === o.id ? T.b2mml : T.line}`, background: fmt === o.id ? "rgba(91,229,132,.08)" : T.bg2 }}>
              <div style={{ color: fmt === o.id ? T.b2mml : T.ink2, marginTop: 1 }}>{o.icon}</div>
              <div>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: T.ink }}>{o.label}</div>
                <div style={{ fontSize: 11.5, color: T.ink2, marginTop: 2, lineHeight: 1.45 }}>{o.desc}</div>
              </div>
              <div style={{ flex: 1 }} />
              {fmt === o.id ? <CheckCircle2 size={18} color={T.b2mml} /> : <Circle size={18} color={T.ink3} />}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 18 }}>
          <Btn color={T.b2mml} active onClick={run}><Download size={14} />Export {fmt === "pdf" ? "PDF" : fmt === "static" ? "static HTML" : "interactive HTML"}</Btn>
          <Btn onClick={onClose}>Cancel</Btn>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   APP SHELL
   ============================================================ */
export default function CLMStudio({ embedded = false, nav = null } = {}) {
  const [tab, setTab] = useState("dashboard");
  const [systems, setSystems] = useState(SEED_SYSTEMS);
  const [maturity, setMaturity] = useState(3);
  const [scope, setScope] = useState({ systems: new Set(), stories: new Set() });
  const [flow, setFlow] = useState({ cpiMode: true, overlays: { plmsi: true, cpi: true, boundary: true, bonames: true, maturity: false }, seqOn: true, fam: "ALL", boFilter: null, activeId: null, activeIface: null });
  const [layout, setLayout] = useState({});
  const [exportOpen, setExportOpen] = useState(false);

  const TABS = [
    { id: "dashboard", label: "Dashboard", icon: LayoutDashboard },
    { id: "setup", label: "Setup", icon: Wand2 },
    { id: "arch", label: "Architecture", icon: Network },
    { id: "flow", label: "Flow Studio", icon: Workflow },
    { id: "systems", label: "Systems", icon: Package },
    { id: "bos", label: "Business Objects", icon: Boxes },
    { id: "impl", label: "Implementation", icon: ListChecks },
    { id: "maturity", label: "Maturity", icon: Gauge },
  ];

  return (
    <div style={{ height: embedded ? "100%" : "100vh", display: "flex", flexDirection: "column", background: T.bg, color: T.ink, fontFamily: FONT_BODY, overflow: "hidden" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Sora:wght@600;700;800&family=Space+Grotesk:wght@400;500;600;700&family=IBM+Plex+Mono:wght@400;500;600&display=swap');
        *{box-sizing:border-box} ::-webkit-scrollbar{width:9px;height:9px} ::-webkit-scrollbar-thumb{background:#1e3047;border-radius:5px} ::-webkit-scrollbar-track{background:transparent}
        select option{background:#0b1220}`}</style>

      <header style={{ display: "flex", alignItems: "center", gap: 18, padding: "12px 20px", borderBottom: `1px solid ${T.line}`, background: T.bg2, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div style={{ width: 34, height: 34, borderRadius: 9, background: `linear-gradient(135deg,${T.plmsi},${T.cpi})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <GitBranch size={19} color="#06101d" />
          </div>
          <div>
            <div style={{ fontFamily: FONT_MONO, fontSize: 9.5, letterSpacing: ".28em", color: T.plmsi, textTransform: "uppercase" }}>Closed-Loop Manufacturing</div>
            <div style={{ fontFamily: FONT_DISPLAY, fontWeight: 800, fontSize: 18, color: T.ink, lineHeight: 1 }}>CLM Studio</div>
          </div>
        </div>
        <nav style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {TABS.map(t => {
            const Ic = t.icon; const on = tab === t.id;
            return (
              <button key={t.id} onClick={() => setTab(t.id)} style={{
                display: "flex", alignItems: "center", gap: 7, padding: "8px 13px", borderRadius: 9, fontFamily: FONT_BODY, fontWeight: 600, fontSize: 12.5, cursor: "pointer",
                color: on ? T.ink : T.ink2, background: on ? "linear-gradient(180deg,rgba(34,211,197,.16),transparent)" : "transparent",
                border: `1px solid ${on ? T.plmsi : "transparent"}`,
              }}><Ic size={15} />{t.label}</button>
            );
          })}
        </nav>
        <div style={{ flex: 1 }} />
        <Btn color={T.b2mml} onClick={() => setExportOpen(true)}><Download size={14} />Export</Btn>
        <Pill color={MATURITY[maturity].color} active>L{maturity} · {MATURITY[maturity].name}</Pill>
        {scope.stories.size > 0 && <Pill color={T.b2mml} active>{scope.stories.size} in scope</Pill>}
        <Pill color={T.plmsi}>{systems.length} systems</Pill>
      </header>

      <main style={{ flex: 1, minHeight: 0 }}>
        {tab === "dashboard" && <Dashboard systems={systems} maturity={maturity} setTab={setTab} scope={scope} />}
        {tab === "setup" && <Setup systems={systems} scope={scope} setScope={setScope} maturity={maturity} setMaturity={setMaturity} goFlow={() => setTab("flow")} />}
        {tab === "arch" && <Architecture systems={systems} layout={layout} setLayout={setLayout} />}
        {tab === "flow" && <FlowStudio systems={systems} scope={scope} flow={flow} setFlow={setFlow} layout={layout} setLayout={setLayout} nav={nav} />}
        {tab === "systems" && <SystemsCatalog systems={systems} setSystems={setSystems} />}
        {tab === "bos" && <BusinessObjectsTab />}
        {tab === "impl" && <Implementation />}
        {tab === "maturity" && <MaturityTab maturity={maturity} setMaturity={setMaturity} />}
      </main>

      {exportOpen && <ExportDialog onClose={() => setExportOpen(false)} getState={() => ({
        generatedAt: new Date().toISOString().slice(0, 10),
        systems, layout,
        businessObjects: BUSINESS_OBJECTS,
        stories: STORIES, phases: PHASES, maturity, maturityModel: MATURITY,
        scope: { systems: [...scope.systems], stories: [...scope.stories] },
        flow: { cpiMode: flow.cpiMode, overlays: flow.overlays, seqOn: flow.seqOn, fam: flow.fam, boFilter: flow.boFilter, scopeOnly: scope.stories.size > 0, activeId: flow.activeId || null },
        geometry: { LANES, LANE_Y, LANE_H, LANE_COLOR, GUT, CANVAS_W, CANVAS_H, NODE_W, NODE_H },
      })} />}
    </div>
  );
}
